(() => {
  const WISHLIST_KEY = 'pcstationlb:wishlist:v1';
  const RECENT_KEY = 'pcstationlb:recent:v1';
  const MAX_RECENT = 8;
  const cache = new Map();
  let wishlistRenderId = 0;

  const read = (key) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch { return []; }
  };
  const write = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
  };
  const unique = (arr) => [...new Set(arr.filter(Boolean))];

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style:'currency', currency }).format((Number(cents)||0)/100);
    } catch {
      return '$' + ((Number(cents)||0)/100).toFixed(2);
    }
  };

  const fetchProduct = async (handle) => {
    if (!handle) return null;
    if (cache.has(handle)) return cache.get(handle);
    const request = (async () => {
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials:'same-origin' });
        if (!res.ok) throw new Error('Product unavailable');
        const product = await res.json();
        return product;
      } catch {
        cache.delete(handle);
        return null;
      }
    })();
    cache.set(handle, request);
    return request;
  };

  const wishlist = () => unique(read(WISHLIST_KEY));
  const recent = () => unique(read(RECENT_KEY));

  const setWishlist = (items) => {
    const clean = unique(items);
    write(WISHLIST_KEY, clean);
    wishlistRenderId += 1;
    syncWishlistUI();
    if (document.querySelector('[data-pcstation-wishlist-drawer]')?.classList.contains('is-open')) {
      renderWishlist();
    }
    document.dispatchEvent(new CustomEvent('pcstation:wishlist-changed', { detail:{ count:clean.length } }));
  };

  const toggleWishlist = (handle) => {
    const items = wishlist();
    setWishlist(items.includes(handle) ? items.filter(x => x !== handle) : [handle, ...items]);
  };

  const syncWishlistUI = () => {
    const items = wishlist();
    const set = new Set(items);
    document.querySelectorAll('[data-pcstation-wishlist-count]').forEach(el => {
      el.textContent = items.length;
      el.hidden = items.length === 0;
      const bubble = el.closest('.cart-count-bubble');
      if (bubble) bubble.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-pcstation-wishlist-toggle]').forEach(btn => {
      const active = set.has(btn.dataset.productHandle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = btn.querySelector('[data-wishlist-label]');
      if (label) label.textContent = active ? 'Saved to wishlist' : 'Save to wishlist';
    });
    document.querySelectorAll('[data-pcstation-wishlist-drawer-count]').forEach(el => {
      el.textContent = items.length + (items.length === 1 ? ' item' : ' items');
    });
  };

  let wishlistReturnFocus = null;
  const openDrawer = () => {
    const drawer = document.querySelector('[data-pcstation-wishlist-drawer]');
    const overlay = document.querySelector('[data-pcstation-wishlist-overlay]');
    if (!drawer) return;
    wishlistReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    drawer.removeAttribute('inert');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden','false');
    if (overlay) overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    renderWishlist();
    requestAnimationFrame(() => drawer.querySelector('[data-pcstation-wishlist-close]')?.focus({ preventScroll: true }));
  };
  const closeDrawer = () => {
    const drawer = document.querySelector('[data-pcstation-wishlist-drawer]');
    const overlay = document.querySelector('[data-pcstation-wishlist-overlay]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    if (wishlistReturnFocus?.isConnected) wishlistReturnFocus.focus({ preventScroll: true });
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden','true');
    if (overlay) overlay.hidden = true;
    document.documentElement.style.overflow = '';
  };

  const renderWishlist = async () => {
    const renderId = ++wishlistRenderId;
    const root = document.querySelector('[data-pcstation-wishlist-items]');
    const empty = document.querySelector('[data-pcstation-wishlist-empty]');
    if (!root) return;
    const items = wishlist();
    if (!items.length) {
      root.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    root.innerHTML = '<div class="pcstation-wishlist-loading">Loading saved products…</div>';
    const products = (await Promise.all(items.map(fetchProduct))).filter(Boolean);
    if (renderId !== wishlistRenderId) return;
    root.innerHTML = products.map(product => {
      const image = product.featured_image || product.images?.[0] || '';
      const variant = product.variants?.find(v => v.available) || product.variants?.[0];
      const price = variant?.price ?? product.price ?? 0;
      return '<article class="pcstation-wishlist-item">' +
        '<a href="/products/' + product.handle + '"><img src="' + image + '" alt=""></a>' +
        '<div class="pcstation-wishlist-item__copy"><a href="/products/' + product.handle + '">' + escapeHtml(product.title) + '</a><strong>' + money(price) + '</strong><small>Saved item</small></div>' +
        '<button type="button" class="pcstation-wishlist-item__remove" data-remove-wishlist="' + product.handle + '" aria-label="Remove ' + escapeAttr(product.title) + ' from wishlist">×</button>' +
      '</article>';
    }).join('');
  };

  const escapeHtml = (str='') => String(str).replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[ch]));
  const escapeAttr = escapeHtml;

  const currentProductHandle = () => {
    const path = location.pathname.match(/\/products\/([^/?#]+)/);
    return path ? decodeURIComponent(path[1]) : '';
  };

  const recordCurrentProduct = () => {
    const handle = currentProductHandle();
    if (!handle) return;
    const items = recent().filter(x => x !== handle);
    write(RECENT_KEY, [handle, ...items].slice(0, MAX_RECENT));
  };

  const renderRecentlyViewed = async () => {
    document.querySelectorAll('[data-pcstation-recently-viewed]').forEach(async section => {
      const track = section.querySelector('[data-pcstation-recent-track]');
      if (!track) return;
      const exclude = section.dataset.currentHandle || '';
      const handles = recent().filter(h => h !== exclude).slice(0, 4);
      if (!handles.length) {
        section.hidden = true;
        track.innerHTML = '';
        return;
      }
      const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);
      if (!products.length) {
        section.hidden = true;
        return;
      }
      track.innerHTML = products.map(product => {
        const image = product.featured_image || product.images?.[0] || '';
        const variant = product.variants?.find(v => v.available) || product.variants?.[0];
        const price = variant?.price ?? product.price ?? 0;
        return '<article class="pcstation-recent-card">' +
          '<button type="button" class="pcstation-wishlist-button" data-pcstation-wishlist-toggle data-product-handle="' + product.handle + '" aria-label="Save ' + escapeAttr(product.title) + ' to wishlist" aria-pressed="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"/></svg></button>' +
          '<a class="pcstation-recent-card__image" href="/products/' + product.handle + '"><img src="' + image + '" alt="' + escapeAttr(product.title) + '"></a>' +
          '<div class="pcstation-recent-card__body"><a href="/products/' + product.handle + '">' + escapeHtml(product.title) + '</a><strong>' + money(price) + '</strong></div>' +
        '</article>';
      }).join('');
      section.hidden = false;
      syncWishlistUI();
    });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-pcstation-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleWishlist(toggle.dataset.productHandle);
      return;
    }
    if (event.target.closest('[data-pcstation-wishlist-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }
    if (event.target.closest('[data-pcstation-wishlist-close]') || event.target.closest('[data-pcstation-wishlist-overlay]')) {
      closeDrawer();
      return;
    }
    const remove = event.target.closest('[data-remove-wishlist]');
    if (remove) {
      setWishlist(wishlist().filter(x => x !== remove.dataset.removeWishlist));
      return;
    }
    if (event.target.closest('[data-pcstation-clear-recent]')) {
      write(RECENT_KEY, []);
      renderRecentlyViewed();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  document.addEventListener('DOMContentLoaded', () => {
    syncWishlistUI();
    renderRecentlyViewed();
    recordCurrentProduct();
    document.querySelectorAll('a[href*="api.whatsapp.com/send"], a[href*="wa.me/"]').forEach((link) => {
      if (!link.hasAttribute('aria-label') && !link.textContent.trim()) link.setAttribute('aria-label', 'Contact PC Station LB on WhatsApp');
    });
  });

  document.addEventListener('pcstation:products-rendered', syncWishlistUI);

  document.addEventListener('shopify:section:load', () => {
    syncWishlistUI();
    renderRecentlyViewed();
  });
})();
